import { API_CONFIG, getAuthHeaders, getCommonHeaders } from "@/config/api";
import { request } from "@umijs/max";

// API Client class with authentication
export class ApiClient {
  private baseURL: string;

  constructor(baseURL: string = API_CONFIG.BASE_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: any): never {
    if (error?.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem("accessToken");
      localStorage.removeItem("user");
      window.location.href = "/user/login";
      throw new Error("Authentication failed. Please login again.");
    }
    throw error;
  }

  /**
   * Make authenticated request
   */
  async authenticatedRequest<T>(
    url: string,
    options: {
      method: string;
      data?: any;
      params?: any;
      skipAuth?: boolean;
    }
  ): Promise<T> {
    try {
      const { method, data, params, skipAuth = false } = options;

      const headers = skipAuth ? getCommonHeaders() : getAuthHeaders();

      return await request<T>(`${this.baseURL}${url}`, {
        method,
        headers,
        data,
        params,
        timeout: API_CONFIG.TIMEOUT,
      });
    } catch (error) {
      this.handleAuthError(error);
    }
  }

  /**
   * GET request with authentication
   */
  async get<T>(
    url: string,
    params?: any,
    skipAuth: boolean = false
  ): Promise<T> {
    return this.authenticatedRequest<T>(url, {
      method: "GET",
      params,
      skipAuth,
    });
  }

  /**
   * POST request with authentication
   */
  async post<T>(
    url: string,
    data?: any,
    skipAuth: boolean = false
  ): Promise<T> {
    return this.authenticatedRequest<T>(url, {
      method: "POST",
      data,
      skipAuth,
    });
  }

  /**
   * PUT request with authentication
   */
  async put<T>(url: string, data?: any): Promise<T> {
    return this.authenticatedRequest<T>(url, {
      method: "PUT",
      data,
    });
  }

  /**
   * PATCH request with authentication
   */
  async patch<T>(url: string, data?: any): Promise<T> {
    return this.authenticatedRequest<T>(url, {
      method: "PATCH",
      data,
    });
  }

  /**
   * DELETE request with authentication. `data` is optional — some DELETE
   * endpoints (e.g. removeFoodsFromTag) need a request body identifying
   * which related rows to remove.
   */
  async delete<T>(url: string, data?: any): Promise<T> {
    return this.authenticatedRequest<T>(url, {
      method: "DELETE",
      data,
    });
  }
}

// Create default API client instance
export const apiClient = new ApiClient();

// Export for convenience
export default apiClient;
